import { Controller } from "@hotwired/stimulus";

// Where the play button sits, and how much room the script element reserves for
// it. Both only apply when the element does not position the button itself; the
// standard position is unchanged from when PK-5 was the only caller.
const BUTTON_VARIANTS = {
  standard: {
    position: "bottom-1/2 sm:right-5 right-4",
    padding: ["sm:pr-12", "pr-9"],
  },
  compact: {
    // The template adds translate-x-1/2, so the gap left at the pill's edge is
    // (offset - buttonWidth/2). Matching the 36px pill's vertical gap of
    // (36 - buttonWidth)/2 solves to an offset of 18px at either button size.
    position: "bottom-1/2 right-[18px]",
    padding: ["sm:pr-8", "pr-7"],
  },
};

export default class extends Controller {
  static targets = [
    "input",
    "form",
    "playIcon",
    "stopIcon",
    "template",
    "compactTemplate",
    "script",
    "toggle",
    "player",
    "failureForm",
  ];
  static values = {
    isPlaying: { type: Boolean, default: false },
    enabledDefault: { type: Boolean, default: false },
    enabled: { type: Boolean, default: false },
    // Whether voiceover is offered on this page at all. The toggle button is
    // rendered server-side and the script targets are not, so without this a
    // stale localStorage value would paint play buttons on a page that has no
    // toggle to turn them off again.
    available: { type: Boolean, default: false },
  };

  connect() {
    // Before the enabled read: assigning enabledValue fires
    // enabledValueChanged, which attaches buttons.
    this.currentAudio = null;
    this.currentButtonId = null;

    const stored = localStorage.getItem("voiceoverEnabled");
    // getItem returns the string "false", which is truthy. Compare explicitly
    // rather than relying on Stimulus's boolean coercion to undo it.
    this.enabledValue =
      stored === null ? this.enabledDefaultValue : stored !== "false";
    this.syncToggle();

    // Watch for changes to the voiceoverplayer div
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          const audioElement = this.hasPlayerTarget
            ? this.playerTarget.querySelector("audio")
            : null;

          if (audioElement && audioElement !== this.currentAudio) {
            // Chrome keeps playing an <audio> that has been detached, so
            // without this the clip being replaced carries on underneath the
            // new one.
            if (this.currentAudio) {
              this.currentAudio.pause();
              this.currentAudio.currentTime = 0;
            }
            this.currentAudio = audioElement;

            // Add event listeners to handle audio state changes
            audioElement.addEventListener("play", () => {
              this.isPlayingValue = true;
              this.updateIcons(this.currentButtonId);
            });

            audioElement.addEventListener("ended", () => {
              this.isPlayingValue = false;
              this.currentAudio = null;
              this.updateIcons(this.currentButtonId);
              this.currentButtonId = null;
            });

            audioElement.addEventListener("pause", () => {
              this.isPlayingValue = false;
              this.updateIcons(this.currentButtonId);
            });
          }
        }
      });
    });

    this.observePlayer();

    // A synthesis failure answers with a JSON error rather than a turbo stream,
    // so no audio ever arrives and the button we were waiting on never resolves.
    this.onSubmitEnd = this.handleSubmitEnd.bind(this);
    if (this.hasFormTarget) {
      this.formTarget.addEventListener("turbo:submit-end", this.onSubmitEnd);
    }

    // Media errors do not bubble, so listen in the capture phase. That covers
    // both the play-button clips in the player and the PK-2 heading audio,
    // which the step components render inside this element.
    this.onMediaError = this.reportMediaError.bind(this);
    this.element.addEventListener("error", this.onMediaError, true);
  }

  // A school filter that blocks the audio host leaves the play button silent
  // with nothing in any log, so report it.
  reportMediaError(event) {
    const audio = event.target;
    if (!(audio instanceof HTMLAudioElement) || !audio.error) return;
    // Layouts that never play audio have no form to report through.
    if (!this.hasFailureFormTarget) return;

    // The form carries the CSRF token; the layout has no meta tag for it.
    const data = new FormData(this.failureFormTarget);
    data.append("error_code", audio.error.code);
    // The code alone cannot tell a blocked host from Chrome refusing the load
    // outright ("URL safety check"); the message can.
    data.append("error_message", audio.error.message || "");
    navigator.sendBeacon(this.failureFormTarget.action, data);
  }

  // The player now lives in the layout, so it outlives the per-step turbo
  // stream -- but bind to it here as well. An observer left pointing at a
  // replaced node reports nothing, and silently: audio still played, while the
  // play/stop icon never flipped and currentAudio stayed null, so clicking
  // again started a second clip instead of stopping the first.
  playerTargetConnected() {
    this.observePlayer();
  }

  observePlayer() {
    if (!this.observer || !this.hasPlayerTarget) return;

    this.observer.disconnect();
    this.observer.observe(this.playerTarget, { childList: true });
  }

  // Each step swaps #checkInContent, which contains the toggle, while <main>
  // and this controller stay put -- so connect() does not run again and the
  // freshly rendered toggle arrives with the server's data-enabled="false".
  // Without this the icon showed muted from the second step onward even though
  // voiceover was on and the play buttons were attached.
  toggleTargetConnected() {
    this.syncToggle();
  }

  // The toggle is only rendered when voiceover is available, so every write to
  // it has to tolerate its absence. Dereferencing it unguarded used to throw
  // here and abandon the rest of connect().
  syncToggle() {
    if (!this.hasToggleTarget) return;

    this.toggleTarget.setAttribute("data-enabled", this.enabledValue);
    this.toggleTarget.setAttribute("aria-pressed", this.enabledValue);
  }

  // Called whenever a script target is connected to the controller
  scriptTargetConnected(element) {
    if (this.enabledValue && this.availableValue) {
      this.attachPlayButton(element);
    }
  }

  // Called whenever a script target is disconnected from the controller
  scriptTargetDisconnected(element) {
    this.detachPlayButton(element);
  }

  // Called when the enabled value changes
  enabledValueChanged() {
    if (this.enabledValue && this.availableValue) {
      this.scriptTargets.forEach((scriptTarget) => {
        this.attachPlayButton(scriptTarget);
      });
    } else {
      this.scriptTargets.forEach((scriptTarget) => {
        this.detachPlayButton(scriptTarget);
      });
    }
  }

  // Helper method to attach play button to a script target
  attachPlayButton(scriptTarget) {
    // Check if play button already exists to avoid duplicates
    if (scriptTarget.querySelector('[data-action*="voiceover#play"]')) {
      return;
    }

    // Pill-shaped choices are shorter than the standard button is tall, so
    // dense pages of them opt into a smaller one.
    const compact =
      scriptTarget.dataset.playButtonCompact !== undefined &&
      this.hasCompactTemplateTarget;
    const variant = compact
      ? BUTTON_VARIANTS.compact
      : BUTTON_VARIANTS.standard;
    const source = compact ? this.compactTemplateTarget : this.templateTarget;
    const template = source.content.cloneNode(true);

    const positionClasses =
      scriptTarget.dataset.playButtonPositionClasses || variant.position;
    const classes = positionClasses.split(" ");

    // loop through the classes and add them to the template
    classes.forEach((cls) => {
      template.firstElementChild.classList.add(cls);
    });

    // Add padding if no custom position classes are provided
    if (!scriptTarget.dataset.playButtonPositionClasses) {
      scriptTarget.classList.add(...variant.padding);
    }

    if (scriptTarget.dataset.voiceoverScriptParam) {
      template.firstElementChild.dataset.voiceoverScriptParam =
        scriptTarget.dataset.voiceoverScriptParam;
    }

    // Add unique identifier to the play button
    const uniqueId = `play-button-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    template.firstElementChild.dataset.playButtonId = uniqueId;

    scriptTarget.appendChild(template);
  }

  detachPlayButton(scriptTarget) {
    const playButton = scriptTarget.querySelector(
      '[data-action*="voiceover#play"]',
    );
    if (playButton) {
      playButton.remove();
    }

    // Remove padding classes that were added
    scriptTarget.classList.remove(
      ...BUTTON_VARIANTS.standard.padding,
      ...BUTTON_VARIANTS.compact.padding,
    );
  }

  play(event) {
    const button = event.currentTarget;
    const buttonId = button.dataset.playButtonId;

    // This prevents the parent element from being triggered when clicking the play button
    event.preventDefault();
    event.stopPropagation();

    // Check if audio is actually playing
    const isCurrentlyPlaying = this.currentAudio && !this.currentAudio.paused;

    if (isCurrentlyPlaying) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.isPlayingValue = false;
      this.updateIcons(buttonId);
      return;
    } else {
      const script =
        event.params?.script || button.parentElement.textContent.trim();
      this.inputTarget.value = script;
      this.currentButtonId = buttonId; // Set the current button ID for audio event listeners
      this.formTarget.requestSubmit();
    }
  }

  // Nothing will be swapped into #voiceoverplayer, so drop the pending button
  // rather than leaving it attributed to whatever plays next.
  handleSubmitEnd(event) {
    if (event.detail?.success) return;

    this.isPlayingValue = false;
    this.updateIcons(this.currentButtonId);
    this.currentButtonId = null;
  }

  updateIcons(buttonId = null) {
    if (buttonId) {
      // Update icons for a specific button
      const button = document.querySelector(
        `[data-play-button-id="${buttonId}"]`,
      );
      if (button) {
        const playIcon = button.querySelector(
          '[data-voiceover-target="playIcon"]',
        );
        const stopIcon = button.querySelector(
          '[data-voiceover-target="stopIcon"]',
        );
        if (playIcon && stopIcon) {
          playIcon.classList.toggle("hidden", this.isPlayingValue);
          stopIcon.classList.toggle("hidden", !this.isPlayingValue);
        }
      }
    } else {
      // Fallback to original behavior for backward compatibility
      if (!this.hasPlayIconTarget || !this.hasStopIconTarget) return;

      this.playIconTarget.classList.toggle("hidden", this.isPlayingValue);
      this.stopIconTarget.classList.toggle("hidden", !this.isPlayingValue);
    }
  }

  playD(event) {
    const script = event.currentTarget.textContent.trim();
    this.inputTarget.value = script;
    this.formTarget.requestSubmit();
  }

  toggleEnabled() {
    this.enabledValue = !this.enabledValue;
    // store the current state in local storage
    localStorage.setItem("voiceoverEnabled", this.enabledValue);
    this.syncToggle();
  }

  disconnect() {
    // One observer per Turbo visit would otherwise be left watching a detached
    // node, and a check-in is twenty-odd visits.
    this.observer?.disconnect();

    if (this.hasFormTarget && this.onSubmitEnd) {
      this.formTarget.removeEventListener("turbo:submit-end", this.onSubmitEnd);
    }
    this.element.removeEventListener("error", this.onMediaError, true);

    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }
  }
}
